// Ticket figures for the Dashboard, computed in the browser from GET_TICKETS as the legacy page did.

export const DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const TERMINAL = new Set(['resolved', 'cancelled', 'canceled']);
const DEFAULT_STATUSES = ['Opened', 'InProgress', 'Waiting4User', 'Waiting4Agent', 'Resolved', 'Cancelled'];

const time = (value) => {
  const t = new Date(value || 0).getTime();
  return Number.isNaN(t) ? 0 : t;
};
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const isPending = (ticket) => !TERMINAL.has(String(ticket.status || '').toLowerCase());

// The legacy team match: flow_type equal to the team or starting with it.
const ticketsForTeam = (tickets, team) => {
  if (!team) return tickets;
  return tickets.filter((t) => String(t.flow_type || '').toLowerCase().startsWith(team));
};

// [{ status, count }] with the four known statuses first; spellings that differ only in case are counted together.
const countByStatus = (tickets) => {
  const counts = new Map(DEFAULT_STATUSES.map((s) => [s.toLowerCase(), { status: s, count: 0 }]));
  tickets.forEach((t) => {
    const status = t.status || 'Unknown';
    const key = status.toLowerCase();
    if (!counts.has(key)) counts.set(key, { status, count: 0 });
    counts.get(key).count += 1;
  });
  return [...counts.values()];
};

// Local days from DAYS-1 days ago to today, so dates match what the user sees in their timezone.
const lastDays = (now) => {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (DAYS - 1));
  return Array.from({ length: DAYS }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
};

// Tickets opened on each day.
const openedPerDay = (tickets, days) => {
  const counts = new Map(days.map((d) => [dayKey(d), 0]));
  tickets.forEach((t) => {
    const opened = time(t.created_at);
    if (!opened) return;
    const key = dayKey(new Date(opened));
    if (counts.has(key)) counts.set(key, counts.get(key) + 1);
  });
  return days.map((d) => ({ date: dayKey(d), day: d, count: counts.get(dayKey(d)) }));
};

// Tickets pending at the end of each day. A ticket that is no longer pending counts as
// closed at updated_at (at created_at when that is missing): an approximation, as before.
const pendingPerDay = (tickets, days) => {
  const spans = tickets
    .map((t) => {
      const openedAt = time(t.created_at);
      if (!openedAt) return null;
      const closedAt = isPending(t) ? null : time(t.updated_at) || openedAt;
      return { openedAt, closedAt };
    })
    .filter(Boolean);
  return days.map((d) => {
    const dayEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
    const count = spans.filter((s) => s.openedAt < dayEnd && (s.closedAt === null || s.closedAt >= dayEnd)).length;
    return { date: dayKey(d), day: d, count };
  });
};

export function ticketSummary(allTickets, team, now = new Date()) {
  const tickets = ticketsForTeam(allTickets, team);
  const byDateDesc = [...tickets].sort((a, b) => time(b.created_at) - time(a.created_at));
  const pending = tickets.filter(isPending).sort((a, b) => time(a.created_at) - time(b.created_at));
  const recentCutoff = now.getTime() - DAYS * DAY_MS;
  const lastMonth = tickets.filter((t) => time(t.created_at) > 0 && time(t.created_at) >= recentCutoff);
  const days = lastDays(now);

  return {
    tickets,
    total: tickets.length,
    totalLastMonth: lastMonth.length,
    byStatus: countByStatus(tickets),
    byStatusLastMonth: countByStatus(lastMonth),
    pendingCount: pending.length,
    recent: byDateDesc.slice(0, 5),
    oldestPending: pending.slice(0, 5),
    opened: openedPerDay(tickets, days),
    pendingDaily: pendingPerDay(tickets, days),
  };
}

// Statistics figures; percentages are null when the base is zero.
export const share = (part, whole) => (whole > 0 ? (part / whole) * 100 : null);
export const formatShare = (value) => (value === null ? '—' : `${value.toFixed(1)}%`);

// Reads `obj[key]` ignoring case, for the per-team maps in the statistics response.
export const valueForTeam = (obj, key) => {
  if (!obj || !key) return undefined;
  const match = Object.keys(obj).find((k) => k.toLowerCase() === String(key).toLowerCase());
  return match ? obj[match] : undefined;
};
